import {
    Box,
    Button as ChakraButton,
    Drawer,
    DrawerBody,
    DrawerCloseButton,
    DrawerContent,
    DrawerHeader,
    DrawerOverlay,
    Flex,
    Icon,
    Text,
    Textarea,
} from '@chakra-ui/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { LuSend, LuTelescope } from 'react-icons/lu';

import { normalizeLocalDateTimeToServer } from '@/utils/date-formatter';
import { GENERIC_ERROR_MESSAGE } from '@/utils/error-utils';
import { Message, MessageBubble } from '@/views/ai-chat/MessageBubble';
import { API_ROOT } from 'env';

import { evaluationPrompt } from './aiEvaluationPrompts';

// The code analysis tool can run silently for up to ~3 minutes between SSE events (see
// CodeAnalysisToolFactory). If nothing arrives for longer than that, assume the connection
// dropped silently (proxy/gateway idle-close) rather than leaving the UI stuck forever.
const STALL_TIMEOUT_MS = 4 * 60 * 1000;
const STALL_MESSAGE = 'This is taking too long — the connection may have dropped. Please try again.';

/* eslint-disable no-console */
// Raw backend exception text may leak internals, so it's never rendered — only logged here
// (dev console) and surfaced as a conversation id, both safe to hand to someone with log access.
const debugAiEval = (message: string, payload?: unknown) => {
    if (!import.meta.env.DEV) {
        return;
    }
    if (payload === undefined) {
        console.debug('[AiEvaluationDrawer]', message);
        return;
    }
    console.debug('[AiEvaluationDrawer]', message, payload);
};
/* eslint-enable no-console */

interface AiEvaluationDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    optimizationId: string;
}

export const AiEvaluationDrawer = ({ isOpen, onClose, optimizationId }: AiEvaluationDrawerProps) => {
    const [hasStarted, setHasStarted] = useState(false);
    const [messages, setMessages] = useState<Message[]>([]);
    const [conversationId, setConversationId] = useState<string | null>(null);
    const [input, setInput] = useState('');
    const [isStreaming, setIsStreaming] = useState(false);
    const esRef = useRef<EventSource | null>(null);
    const conversationIdRef = useRef<string | null>(null);
    const scrollRef = useRef<HTMLDivElement>(null);
    const lastActivityRef = useRef<number>(0);

    const reset = useCallback(() => {
        esRef.current?.close();
        esRef.current = null;
        setHasStarted(false);
        setMessages([]);
        setConversationId(null);
        conversationIdRef.current = null;
        setInput('');
        setIsStreaming(false);
    }, []);

    useEffect(() => {
        if (!isOpen) {
            reset();
        }
    }, [isOpen, reset]);

    useEffect(() => {
        return () => {
            esRef.current?.close();
        };
    }, []);

    useEffect(() => {
        const el = scrollRef.current;
        if (el) {
            el.scrollTop = el.scrollHeight;
        }
    }, [messages]);

    // Safety net: if the stream goes silent for too long (dropped proxy connection, hung
    // request), stop waiting instead of leaving the thinking indicator spinning forever.
    useEffect(() => {
        if (!isStreaming) return;
        const interval = setInterval(() => {
            if (Date.now() - lastActivityRef.current < STALL_TIMEOUT_MS) return;
            debugAiEval('Stall timeout reached, aborting stream', { conversationId: conversationIdRef.current });
            esRef.current?.close();
            esRef.current = null;
            setIsStreaming(false);
            setMessages((prev) =>
                prev.map((m) =>
                    m.role === 'assistant' && m.isStreaming
                        ? { ...m, isStreaming: false, isError: true, content: m.content || STALL_MESSAGE }
                        : m,
                ),
            );
        }, 5000);
        return () => clearInterval(interval);
    }, [isStreaming]);

    const sendMessage = useCallback((messageText: string) => {
        const trimmed = messageText.trim();
        if (!trimmed || esRef.current) {
            return;
        }

        const userMessage: Message = { id: crypto.randomUUID(), role: 'user', content: trimmed, parts: [] };
        const assistantMessageId = crypto.randomUUID();
        const assistantMessage: Message = {
            id: assistantMessageId,
            role: 'assistant',
            content: '',
            parts: [],
            isStreaming: true,
        };
        setMessages((prev) => [...prev, userMessage, assistantMessage]);
        setIsStreaming(true);

        const updateAssistant = (updater: (msg: Message) => Message) => {
            setMessages((prev) => prev.map((m) => (m.id === assistantMessageId ? updater(m) : m)));
        };

        const params = new URLSearchParams({ message: trimmed, pmMode: 'true' });
        if (conversationIdRef.current) {
            params.set('chatConversationGUID', conversationIdRef.current);
        }

        const es = new EventSource(`${API_ROOT}/customer/chat/stream?${params.toString()}`, { withCredentials: true });
        esRef.current = es;
        lastActivityRef.current = Date.now();
        let terminalEventHandled = false;

        const finishStream = () => {
            esRef.current = null;
            setIsStreaming(false);
            es.close();
        };

        es.addEventListener('conversation_id', (event: MessageEvent) => {
            lastActivityRef.current = Date.now();
            conversationIdRef.current = event.data;
            setConversationId(event.data);
        });

        es.addEventListener('token', (event: MessageEvent) => {
            lastActivityRef.current = Date.now();
            const { token }: { token: string } = JSON.parse(event.data);
            updateAssistant((msg) => {
                const parts = [...msg.parts];
                const last = parts[parts.length - 1];
                if (last?.type === 'token') {
                    parts[parts.length - 1] = { type: 'token', content: last.content + token };
                } else {
                    parts.push({ type: 'token', content: token });
                }
                return { ...msg, content: msg.content + token, parts };
            });
        });

        es.addEventListener('tool_call', (event: MessageEvent) => {
            lastActivityRef.current = Date.now();
            const { tool, message }: { tool: string; message: string } = JSON.parse(event.data);
            updateAssistant((msg) => {
                const parts = [...msg.parts];
                const last = parts[parts.length - 1];
                if (last?.type === 'tool_call') {
                    parts[parts.length - 1] = { type: 'tool_call', tool, message };
                } else {
                    parts.push({ type: 'tool_call', tool, message });
                }
                return { ...msg, parts };
            });
        });

        es.addEventListener('done', (event: MessageEvent) => {
            if (terminalEventHandled) return;
            terminalEventHandled = true;
            const doneTimestamp = event.data || new Date().toISOString();
            updateAssistant((msg) => ({
                ...msg,
                isStreaming: false,
                timestamp: normalizeLocalDateTimeToServer(doneTimestamp),
            }));
            finishStream();
        });

        es.addEventListener('error', (event: MessageEvent) => {
            if (terminalEventHandled) return;
            terminalEventHandled = true;
            const rawError = event.data || '';
            debugAiEval('Received error event', { rawError, conversationId: conversationIdRef.current });
            const errorMessage = rawError.includes('Max tool call rounds exceeded')
                ? 'I could not finish this request within the allowed number of steps. Narrowing the question, for example to one household or date range, usually helps.'
                : GENERIC_ERROR_MESSAGE;
            updateAssistant((msg) => ({ ...msg, content: errorMessage, isStreaming: false, isError: true }));
            finishStream();
        });

        es.onerror = () => {
            if (terminalEventHandled) return;
            terminalEventHandled = true;
            debugAiEval('EventSource onerror fired', { conversationId: conversationIdRef.current });
            updateAssistant((msg) => ({
                ...msg,
                content: msg.content || 'Something went wrong. Please try again.',
                isStreaming: false,
                isError: true,
            }));
            finishStream();
        };
    }, []);

    const handleStartEvaluation = () => {
        setHasStarted(true);
        sendMessage(evaluationPrompt(optimizationId));
    };

    const handleSubmit = () => {
        sendMessage(input);
        setInput('');
    };

    return (
        <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="xl">
            <DrawerOverlay />
            <DrawerContent>
                <DrawerCloseButton />
                <DrawerHeader borderBottomWidth={1}>AI evaluation — optimization #{optimizationId}</DrawerHeader>
                <DrawerBody display="flex" flexDirection="column" overflow="hidden" p={4}>
                    {!hasStarted ? (
                        <Flex
                            as="button"
                            onClick={handleStartEvaluation}
                            direction="column"
                            gap={1}
                            p={4}
                            borderWidth={1}
                            borderRadius="md"
                            textAlign="left"
                            _hover={{ borderColor: 'teal.400' }}
                        >
                            <Flex align="center" gap={2} fontWeight="semibold">
                                <Icon as={LuTelescope} />
                                <Text>Evaluate trades</Text>
                            </Flex>
                            <Text color="gray.500" fontSize="sm">
                                Review the optimization for systemic anomalies and execution safety.
                            </Text>
                        </Flex>
                    ) : (
                        <Flex direction="column" flex="1" minHeight={0} gap={3}>
                            <Flex justify="space-between" align="center" flexShrink={0}>
                                <Text color="gray.400" fontSize="sm">
                                    Trade-safety evaluation
                                </Text>
                                <ChakraButton size="xs" variant="ghost" onClick={reset}>
                                    {isStreaming ? 'Cancel' : 'Start over'}
                                </ChakraButton>
                            </Flex>
                            {/* MessageBubble/Textarea are styled for the (light-only) admin app; give this
                                a light surface here rather than the dark cip theme so text stays readable. */}
                            <Flex
                                direction="column"
                                flex="1"
                                minHeight={0}
                                bg="white"
                                borderRadius="md"
                                overflow="hidden"
                            >
                                <Box ref={scrollRef} flex="1" minHeight={0} overflowY="auto" p={4}>
                                    <Flex direction="column" gap={6}>
                                        {messages.map((message) => (
                                            <MessageBubble
                                                key={message.id}
                                                message={message}
                                                conversationId={conversationId}
                                            />
                                        ))}
                                        {/* The raw exception text is deliberately never rendered (may carry
                                            backend internals), but the conversation id is safe to show — it's
                                            what ChatService's logs and Datadog LLMObs spans are keyed by, so
                                            it's the fastest way to find the real error server-side. */}
                                        {messages.at(-1)?.isError && conversationId && (
                                            <Text color="gray.400" fontSize="xs" fontFamily="mono">
                                                Conversation: {conversationId}
                                            </Text>
                                        )}
                                    </Flex>
                                </Box>
                                <Box borderTopWidth={1} borderColor="gray.200" p={3} flexShrink={0}>
                                    <Textarea
                                        value={input}
                                        onChange={(e) => setInput(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && !e.shiftKey) {
                                                e.preventDefault();
                                                handleSubmit();
                                            }
                                        }}
                                        placeholder="Ask a follow-up…"
                                        size="sm"
                                        resize="none"
                                        rows={2}
                                        isReadOnly={isStreaming}
                                        opacity={isStreaming ? 0.6 : 1}
                                        cursor={isStreaming ? 'not-allowed' : 'text'}
                                        bg="white"
                                        color="gray.800"
                                        _placeholder={{ color: 'gray.400' }}
                                    />
                                    <Flex justify="flex-end" mt={2}>
                                        <ChakraButton
                                            size="sm"
                                            colorScheme="teal"
                                            leftIcon={<Icon as={LuSend} />}
                                            onClick={handleSubmit}
                                            isDisabled={!input.trim() || isStreaming}
                                        >
                                            Send
                                        </ChakraButton>
                                    </Flex>
                                </Box>
                            </Flex>
                        </Flex>
                    )}
                </DrawerBody>
            </DrawerContent>
        </Drawer>
    );
};
